import React from "react";
import "./Table.scss";

export type Column<T = any> = {
  key: string;
  title: React.ReactNode;
  width?: string | number;
  align?: "left" | "center" | "right";
  sortable?: boolean;
  // custom cell renderer: (value, row, rowIndex) => ReactNode
  render?: (value: any, row: T, rowIndex: number) => React.ReactNode;
};

export type Pagination = {
  pageIndex: number; // zero-based
  pageSize: number;
  total?: number;
};

type Props<T = any> = {
  columns: Column<T>[];
  data: T[];
  rowKey?: string | ((row: T) => string);
  className?: string;
  loading?: boolean;
  emptyText?: React.ReactNode;
  selectable?: boolean;
  onSelectionChange?: (selectedRows: T[]) => void;
  // pagination (controlled) - if provided, table uses these and emits changes
  pagination?: Pagination;
  onPageChange?: (pageIndex: number, pageSize: number) => void;
  pageSizeOptions?: number[];
  // optional top-right actions area
  headerRight?: React.ReactNode;
};

function defaultRowKey<T>(row: T, idx: number) {
  if (
    typeof (row as any).id === "string" ||
    typeof (row as any).id === "number"
  )
    return String((row as any).id);
  return String(idx);
}

export default function Table<T = any>(props: Props<T>) {
  const {
    columns,
    data,
    rowKey,
    className,
    loading = false,
    emptyText = "No results",
    // onSelectionChange,
    pagination,
    onPageChange,
    pageSizeOptions = [10, 25, 50, 100],
    headerRight,
  } = props;

  //   const [localSelected, setLocalSelected] = useState<Record<string, boolean>>(
  //     {}
  //   );

  const resolvedRowKey = (row: T, idx: number) => {
    if (typeof rowKey === "function") return rowKey(row);
    if (typeof rowKey === "string")
      return (row as any)[rowKey] ?? defaultRowKey(row, idx);
    return defaultRowKey(row, idx);
  };

  //   const selectedRows = useMemo(() => {
  //     const keys = Object.keys(localSelected).filter((k) => localSelected[k]);
  //     return data.filter((row, idx) => keys.includes(resolvedRowKey(row, idx)));
  //   }, [localSelected, data]);

  //   React.useEffect(() => {
  //     if (onSelectionChange) onSelectionChange(selectedRows);
  //   }, [selectedRows]);

  //   const toggleSelectAll = (checked: boolean) => {
  //     const next: Record<string, boolean> = {};
  //     if (checked) {
  //       data.forEach((row, idx) => {
  //         next[resolvedRowKey(row, idx)] = true;
  //       });
  //     }
  //     setLocalSelected(next);
  //   };

  //   const toggleRow = (key: string) => {
  //     setLocalSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  //   };

  const pageIndex = pagination?.pageIndex ?? 0;
  const pageSize = pagination?.pageSize ?? data.length;
  const total = pagination?.total ?? data.length;
  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = Number(e.target.value);
    if (onPageChange) onPageChange(0, newSize);
  };

  const goToPage = (newIndex: number) => {
    if (onPageChange) onPageChange(newIndex, pageSize);
  };

  return (
    <div className={`cg-table-wrapper ${className ?? ""}`}>
      <div className="cg-table-header">
        <div className="cg-table-title" />
        {headerRight && (
          <div className="cg-table-header-right">{headerRight}</div>
        )}
      </div>

      <div className="cg-table">
        <table>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={String(col.key)}
                  style={{ width: col.width }}
                  className={`cg-th ${
                    col.align ? `cg-align-${col.align}` : ""
                  }`}
                >
                  <div className="cg-th-inner">
                    <span className="cg-th-title">{col.title}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr className="cg-row-loading">
                <td colSpan={columns.length}>
                  <div className="cg-loading">Loading...</div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="cg-empty">
                  {emptyText}
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                const key = resolvedRowKey(row, rowIndex);
                return (
                  <tr key={key} className="cg-row">
                    {columns.map((col) => (
                      <td
                        key={col.key as React.Key}
                        className={`cg-td ${
                          col.align ? `cg-align-${col.align}` : ""
                        }`}
                      >
                        {col.render
                          ? col.render((row as any)[col.key], row, rowIndex)
                          : (row as any)[col.key]}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className="cg-table-footer">
          <div className="cg-pagination-left">
            <span className="cg-pagination-info">
              Results: {Math.min(total, pageIndex * pageSize + 1)}-
              {Math.min(total, (pageIndex + 1) * pageSize)} of {total}
            </span>
          </div>
          <div className="cg-pagination-controls">
            <select
              value={pageSize}
              onChange={handlePageSizeChange}
              className="cg-page-size-select"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt} / page
                </option>
              ))}
            </select>

            <button
              className="cg-page-btn"
              onClick={() => goToPage(Math.max(0, pageIndex - 1))}
              disabled={pageIndex === 0}
            >
              ‹
            </button>
            <div className="cg-page-numbers">
              {Array.from({
                length: Math.max(1, Math.ceil(total / pageSize)),
              }).map((_, i) => (
                <button
                  key={i}
                  className={`cg-page-number ${
                    i === pageIndex ? "active" : ""
                  }`}
                  onClick={() => goToPage(i)}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button
              className="cg-page-btn"
              onClick={() =>
                goToPage(
                  Math.min(Math.ceil(total / pageSize) - 1, pageIndex + 1)
                )
              }
              disabled={(pageIndex + 1) * pageSize >= total}
            >
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export { Table };
